import type { BasketItem } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";

/** Norce rejects a basket line comment longer than this. */
export const LINE_COMMENT_MAX_LENGTH = 255;

export interface ConfiguredOption {
	group: string;
	value: string;
}

/** Code of the Norce BasketItem/OrderItem info type for the picked options. */
export const OPTIONS_INFO_CODE = "configured_options";

type LineInfo = NonNullable<BasketItem["Info"]>[number];

const formatOptions = (options: ConfiguredOption[]) =>
	options.map(({ group, value }) => `${group}: ${value}`).join("; ");

/** The full options as line info, which carries over to the order line. */
export const optionsInfo = (options: ConfiguredOption[]): LineInfo[] =>
	options.length
		? // TypeId differs between Norce tiers; Code alone is enough.
			[{ Code: OPTIONS_INFO_CODE, Value: formatOptions(options) } as LineInfo]
		: [];

/** `Group: Value; …`, falling back to values only when that is over the limit. */
export const formatOptionsComment = (options: ConfiguredOption[]): string => {
	const full = formatOptions(options);
	if (full.length <= LINE_COMMENT_MAX_LENGTH) return full;

	return options
		.map(({ value }) => value)
		.join("; ")
		.slice(0, LINE_COMMENT_MAX_LENGTH)
		.trimEnd();
};

const normalizedComment = (comment: string | null | undefined) =>
	comment?.trim() || null;

/** Same product with the same options, so a configuration only merges into itself. */
export const isSameBasketLine = (
	line: Partial<BasketItem>,
	item: Partial<BasketItem>
): boolean =>
	line.PartNo === item.PartNo &&
	normalizedComment(line.Comment) === normalizedComment(item.Comment);

/** Key for grouping lines of the same product and configuration. */
export const basketLineKey = (line: Partial<BasketItem>): string =>
	`${line.ProductId}|${normalizedComment(line.Comment) ?? ""}`;

/** Norce clears a line's comment and manual price on update unless they are sent again. */
export const lineConfiguration = (
	line: Partial<BasketItem> | undefined
): Partial<BasketItem> => {
	if (!line?.Comment && !line?.IsPriceManual) return {};

	return {
		Comment: line.Comment,
		// The price before basket discounts, so a discount is never saved into it.
		...(line.IsPriceManual && line.PriceOriginal != null
			? { Price: line.PriceOriginal, IsPriceManual: true }
			: {})
	};
};
